import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Pages
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import CalendarPage from './pages/CalendarPage';
import TasksPage from './pages/TasksPage';
import TaskDetailPage from './pages/TaskDetailPage';
import ProfilePage from './pages/ProfilePage';
import WorkerTracking from './pages/WorkerTracking';

// Components
import BottomNavigation from './components/BottomNavigation';
import ProtectedRoute from './components/ProtectedRoute';
import WorkerDashboard from './components/WorkerDashboard';
import ManagerQADashboard from './components/ManagerQADashboard';
import AdminDashboard from './components/AdminDashboard';

// Services
import ApiService from './services/api';

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Nema više `localStorage`-a: pitamo server ko je ulogovan. Access token
    // je u `HttpOnly` kolačiću, a ako je istekao, `ApiService` će pokušati
    // refresh kolačićem pre nego što prijavi da sesija ne postoji.
    const checkAuth = async () => {
      try {
        const userData = await ApiService.getCurrentUser();
        setUser(userData);
        setIsAuthenticated(true);
      } catch (err) {
        setIsAuthenticated(false);
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, []);

  const handleLogin = (userData) => {
    setUser(userData);
    setIsAuthenticated(true);
    toast.success('Logged in successfully!');
  };

  const handleLogout = async () => {
    // Server mora da opozove tokene i obriše kolačiće — samo brisanje
    // lokalnog stanja bi ostavilo važeću sesiju na serveru.
    try {
      await ApiService.logout();
    } catch (err) {
      console.error('Logout request failed:', err);
    }
    setUser(null);
    setIsAuthenticated(false);
    toast.info('Logged out');
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <Router>
      <div className="app-container">
        <ToastContainer
          position="bottom-center"
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
        />

        <Routes>
          <Route
            path="/login"
            element={
              isAuthenticated ? (
                <Navigate to="/dashboard" replace />
              ) : (
                <LoginPage onLogin={handleLogin} />
              )
            }
          />

          {/* Protected routes */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute isAuthenticated={isAuthenticated}>
                <DashboardPage user={user} />
              </ProtectedRoute>
            }
          />

          <Route
            path="/worker"
            element={
              <ProtectedRoute isAuthenticated={isAuthenticated}>
                <WorkerDashboard user={user} />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/users"
            element={
              <ProtectedRoute isAuthenticated={isAuthenticated}>
                {user?.role === 'admin' ? (
                  <AdminDashboard />
                ) : (
                  <Navigate to="/dashboard" replace />
                )}
              </ProtectedRoute>
            }
          />

          <Route
            path="/calendar"
            element={
              <ProtectedRoute isAuthenticated={isAuthenticated}>
                <CalendarPage user={user} />
              </ProtectedRoute>
            }
          />

          <Route
            path="/tasks"
            element={
              <ProtectedRoute isAuthenticated={isAuthenticated}>
                <TasksPage user={user} />
              </ProtectedRoute>
            }
          />

          <Route
            path="/tasks/:id"
            element={
              <ProtectedRoute isAuthenticated={isAuthenticated}>
                <TaskDetailPage user={user} />
              </ProtectedRoute>
            }
          />

          <Route
            path="/tracking"
            element={
              <ProtectedRoute isAuthenticated={isAuthenticated}>
                <WorkerTracking user={user} />
              </ProtectedRoute>
            }
          />

          <Route
            path="/profile"
            element={
              <ProtectedRoute isAuthenticated={isAuthenticated}>
                <ProfilePage user={user} onLogout={handleLogout} />
              </ProtectedRoute>
            }
          />

          <Route path="/" element={<Navigate to="/dashboard" replace />} />
        </Routes>

        {isAuthenticated && <BottomNavigation user={user} />}
      </div>
    </Router>
  );
}

export default App;